import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { AdSchedule } from '@jwp/ott-common/types/ad-schedule';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import type { Jwpltx } from '@jwp/ott-common/types/jwpltx';
import { useConfigStore } from '@jwp/ott-common/src/stores/ConfigStore';
import { testId } from '@jwp/ott-common/src/utils/common';
import { formatDurationTag } from '@jwp/ott-common/src/utils/formatting';
import env from '@jwp/ott-common/src/env';
import useEventCallback from '@jwp/ott-hooks-react/src/useEventCallback';
import { useMediaSources } from '@jwp/ott-hooks-react/src/useMediaSources';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import Close from '@jwp/ott-theme/assets/icons/close.svg?react';
import ChevronLeft from '@jwp/ott-theme/assets/icons/chevron_left.svg?react';
import ChevronRight from '@jwp/ott-theme/assets/icons/chevron_right.svg?react';
import ChevronUp from '@jwp/ott-theme/assets/icons/chevron_up.svg?react';
import {
  Player,
  buildDrmConfig,
  isLiveStreamPlayback,
  pickPlayableSource,
  type SdkPlayerController,
} from '@jwp/ott-player-sdk';
import { useTranslation } from 'react-i18next';

import NextCountdownOverlay from '../NextCountdownOverlay/NextCountdownOverlay';
import Icon from '../Icon/Icon';
import CustomPlayerOverlays from './CustomPlayerOverlays';
import CustomPlayerAdChrome, { type AdPlaybackState } from './CustomPlayerAdChrome';
import styles from './CustomPlayer.module.scss';

/** Minimal WebVTT so the video element has a captions track when the API does not list any (a11y / Sonar). */
const PLACEHOLDER_CAPTIONS_TRACK =
  'data:text/vtt;charset=utf-8,' + encodeURIComponent('WEBVTT\n\n');

/** Watch-history and app code expect this surface; it matches `SdkPlayerController`. */
export type CustomPlayerController = SdkPlayerController;

type Props = {
  item: PlaylistItem;
  feedId?: string;
  startTime?: number;
  autostart?: boolean;
  videoThumnail?: string;
  adsData?: AdSchedule;
  onReady?: (player?: CustomPlayerController) => void;
  onPlay?: () => void;
  onPause?: () => void;
  onComplete?: () => void;
  onUserActive?: () => void;
  onUserInActive?: () => void;
  onBeforePlay?: () => void;
  onFirstFrame?: () => void;
  onRemove?: () => void;
  onNext?: () => void;
  onAdBreakStart?: () => void;
  onAdBreakEnd?: () => void;
  nextItem?: PlaylistItem;
  showMoreLikeThisButton?: boolean;
  relatedVideos?: PlaylistItem[];
  onRelatedVideoClick?: (item: PlaylistItem) => void;
  isAutoPlayNextVideo?: boolean;
  ReConnecting: string;
};

type PlaybackTimeEvent = {
  position: number;
  duration: number;
};

type AdTimeEvent = AdPlaybackState;

type MoreLikeThisButtonProps = {
  containerRef: React.RefObject<HTMLDivElement | null>;
  label: string;
  onClick: (video?: HTMLVideoElement | null) => void;
  videoRef: React.RefObject<HTMLVideoElement | null>;
};

const MoreLikeThisButton: React.FC<MoreLikeThisButtonProps> = ({ containerRef, label, onClick, videoRef }) => {
  const [controlsVisible, setControlsVisible] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let controlsObserver: MutationObserver | null = null;

    const syncControlsVisibility = () => {
      const controls = container.querySelector('.shaka-controls-container');
      if (!controls) {
        setControlsVisible(false);
        return;
      }

      const updateControlsVisible = () => {
        const shown = controls.getAttribute('shown') === 'true';
        const casting = controls.getAttribute('casting') === 'true';
        const notHidden = !controls.classList.contains('shaka-hidden');
        setControlsVisible(shown || casting || notHidden);
      };

      updateControlsVisible();

      controlsObserver?.disconnect();
      controlsObserver = new MutationObserver(updateControlsVisible);
      controlsObserver.observe(controls, { attributes: true, attributeFilter: ['shown', 'casting', 'class'] });
    };

    syncControlsVisibility();

    const containerObserver = new MutationObserver(syncControlsVisibility);
    containerObserver.observe(container, { childList: true, subtree: true });

    return () => {
      containerObserver.disconnect();
      controlsObserver?.disconnect();
    };
  }, [containerRef]);

  if (!controlsVisible) return null;

  return (
    <button
      type="button"
      className={styles.moreLikeThisButton}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick(videoRef.current);
      }}
    >
      <ChevronUp />
      {label}
    </button>
  );
};

/** Brand-styled live pill, anchored top-left so it sits above Shaka UI's chrome without overlapping the control bar. */
const LiveBadge: React.FC = () => {
  const { t } = useTranslation('video');
  return (
    <div className={styles.liveBadgeWrap} data-testid={testId('custom-player-live-badge')}>
      <span className={styles.liveBadge} aria-live="polite">
        <span className={styles.liveDot} aria-hidden />
        {t('live')}
      </span>
    </div>
  );
};

const CustomPlayer: React.FC<Props> = ({
  item,
  feedId: _feedId,
  startTime = 0,
  autostart,
  videoThumnail,
  adsData,
  onReady,
  onPlay,
  onPause,
  onComplete,
  onUserActive: _onUserActive,
  onUserInActive: _onUserInActive,
  onBeforePlay,
  onFirstFrame,
  onRemove,
  onNext: _onNext,
  onAdBreakStart,
  onAdBreakEnd,
  nextItem,
  showMoreLikeThisButton,
  relatedVideos = [],
  onRelatedVideoClick,
  isAutoPlayNextVideo,
  ReConnecting,
}) => {
  const { t } = useTranslation('video');
  const [sessionPlayer, setSessionPlayer] = useState<SdkPlayerController | null>(null);
  /** Hide our overlay loader after first successful load so rebuffering doesn’t cover Shaka's chrome. */
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [adPlayback, setAdPlayback] = useState<AdPlaybackState | null>(null);
  const [nextCountdownTime, setNextCountdownTime] = useState<number | null>(null);
  const [showNextOverlay, setShowNextOverlay] = useState(true);
  const [showMoreLikeThisOverlay, setShowMoreLikeThisOverlay] = useState(false);
  const [canScrollMoreLikeThisLeft, setCanScrollMoreLikeThisLeft] = useState(false);
  const [canScrollMoreLikeThisRight, setCanScrollMoreLikeThisRight] = useState(false);
  const nextVideoInTime = 10;
  const setIsOverlayManuallyClosedRef = useRef(false);
  const lastPlaybackRef = useRef({ position: 0, duration: 0 });
  const isPlayingRef = useRef(false);
  const moreLikeThisRowRef = useRef<HTMLDivElement | null>(null);

  const isAdPlaying = adPlayback !== null;

  const { adConfigLevel, featuredAdDeliveryMethod, isAdEnabled } = useAppConfig();
  const configAdDeliveryMethod = useConfigStore((s) => s.config.adDeliveryMethod);
  const analyticsToken = useConfigStore((s) => s.config.analyticsToken);
  const adDeliveryMethod = featuredAdDeliveryMethod || configAdDeliveryMethod;
  const isAdConfigLevelPresent =
    adConfigLevel !== 'none' && adConfigLevel != null && adConfigLevel !== undefined && adConfigLevel !== '';

  const isCsai = useMemo(
    () =>
      !!adsData?.schedule &&
      adDeliveryMethod !== 'ssai' &&
      (item.is_ssai_enabled ?? 'false') !== 'true' &&
      (isAdConfigLevelPresent || isAdEnabled),
    [adsData?.schedule, adDeliveryMethod, item.is_ssai_enabled, isAdConfigLevelPresent, isAdEnabled],
  );

  const adsConfig = useMemo(
    () => (isCsai && adsData?.schedule ? { schedule: adsData.schedule, client: adsData.client } : undefined),
    [isCsai, adsData?.schedule, adsData?.client],
  );

  const playbackMediaKey = useMemo(
    () => (isCsai && adsData?.schedule ? `${item.mediaid}:${adsData.schedule}` : item.mediaid),
    [isCsai, adsData?.schedule, item.mediaid],
  );

  const { sources } = useMediaSources({ item, baseUrl: env.APP_API_BASE_URL });

  const isLiveStream = useMemo(() => isLiveStreamPlayback(item), [item]);

  /** `useMediaSources` replaces the `sources` array often; key by content so Shaka is not destroyed every refresh. */
  const playbackKey = useMemo(() => {
    const selected = pickPlayableSource(sources);
    if (!selected) return '';
    return [
      selected.file,
      selected.type ?? '',
      selected.drm?.widevine?.url ?? '',
      selected.drm?.playready?.url ?? '',
      selected.drm?.fairplay?.processSpcUrl ?? '',
      selected.drm?.fairplay?.certificateUrl ?? '',
    ].join('\0');
  }, [sources]);

  const { manifestUrl, drmConfig } = useMemo(() => {
    const selected = pickPlayableSource(sources);
    if (!selected) return { manifestUrl: null as string | null, drmConfig: null };
    return { manifestUrl: selected.file, drmConfig: buildDrmConfig(selected.drm) };
  }, [playbackKey]);

  const handleReady = useEventCallback((player?: SdkPlayerController) => {
    if (player) setSessionPlayer(player);
    onReady?.(player);
  });

  useEffect(() => {
    setSessionPlayer(null);
  }, [item.mediaid]);

  useEffect(() => {
    setInitialLoadComplete(false);
    setAdPlayback(null);
    setNextCountdownTime(null);
    setShowNextOverlay(true);
    setShowMoreLikeThisOverlay(false);
    setIsOverlayManuallyClosedRef.current = false;
    lastPlaybackRef.current = { position: 0, duration: 0 };
  }, [item.mediaid, playbackKey]);

  useEffect(() => {
    if (!sessionPlayer || isLiveStream || !isAutoPlayNextVideo || !nextItem) return undefined;

    const onTime = (...args: unknown[]) => {
      const event = args[0] as PlaybackTimeEvent;
      const currentTime = Math.floor(event.position);
      const duration = Math.floor(event.duration);
      lastPlaybackRef.current = { position: currentTime, duration };

      if (duration <= 0) return;

      const timeRemaining = duration - currentTime;

      if (timeRemaining <= nextVideoInTime && timeRemaining > 0) {
        if (!setIsOverlayManuallyClosedRef.current) {
          setNextCountdownTime(Math.ceil(timeRemaining));
          setShowNextOverlay(true);
        }
      } else {
        setNextCountdownTime(null);
        setShowNextOverlay(false);
        setIsOverlayManuallyClosedRef.current = false;
      }
    };

    sessionPlayer.on('time', onTime);
    return () => sessionPlayer.off('time', onTime);
  }, [sessionPlayer, isLiveStream, isAutoPlayNextVideo, nextItem, nextVideoInTime]);

  const handleCloseOverlay = useEventCallback(() => {
    setShowNextOverlay(false);
    setIsOverlayManuallyClosedRef.current = true;
    setNextCountdownTime(null);
  });

  const handleSkipToEnd = useEventCallback(() => {
    if (!sessionPlayer) return;
    const { duration } = lastPlaybackRef.current;
    if (duration <= 0) return;

    if (isPlayingRef.current) {
      sessionPlayer.seek(duration - 0.1);
      return;
    }

    const onPlay = () => {
      sessionPlayer.seek(duration - 0.1);
      sessionPlayer.off('play', onPlay);
    };
    sessionPlayer.on('play', onPlay);
    void sessionPlayer.play();
  });

  useEffect(() => {
    if (!sessionPlayer) return undefined;
    const onLoaded = () => setInitialLoadComplete(true);
    sessionPlayer.on('loaded', onLoaded);
    return () => sessionPlayer.off('loaded', onLoaded);
  }, [sessionPlayer]);

  useEffect(() => {
    if (!sessionPlayer) return undefined;
    const onPlayEvent = () => {
      isPlayingRef.current = true;
      onPlay?.();
    };
    const onPauseEvent = () => {
      isPlayingRef.current = false;
      onPause?.();
    };
    sessionPlayer.on('play', onPlayEvent);
    sessionPlayer.on('pause', onPauseEvent);
    return () => {
      sessionPlayer.off('play', onPlayEvent);
      sessionPlayer.off('pause', onPauseEvent);
    };
  }, [sessionPlayer, onPlay, onPause]);

  useEffect(() => {
    if (!sessionPlayer || !onComplete) return undefined;
    const h = () => onComplete();
    sessionPlayer.on('complete', h);
    return () => sessionPlayer.off('complete', h);
  }, [sessionPlayer, onComplete]);

  useEffect(() => {
    if (!sessionPlayer || !onFirstFrame) return undefined;
    const h = () => onFirstFrame();
    sessionPlayer.on('firstFrame', h);
    return () => sessionPlayer.off('firstFrame', h);
  }, [sessionPlayer, onFirstFrame]);

  useEffect(() => {
    if (!sessionPlayer) return undefined;

    const onAdBreakStartHandler = () => {
      setAdPlayback(
        (prev) =>
          prev ?? {
            position: 0,
            duration: 0,
            remaining: 0,
            sequencePosition: 1,
            sequenceLength: 1,
            canSkip: false,
            isSkippable: false,
            skipInSeconds: 0,
          },
      );
      onAdBreakStart?.();
    };
    const onAdBreakEndHandler = () => {
      setAdPlayback(null);
      onAdBreakEnd?.();
    };
    const onAdTime = (...args: unknown[]) => {
      const event = args[0] as AdTimeEvent;
      if (!event.duration) return;
      setAdPlayback(event);
    };

    sessionPlayer.on('adBreakStart', onAdBreakStartHandler);
    sessionPlayer.on('adBreakEnd', onAdBreakEndHandler);
    sessionPlayer.on('adTime', onAdTime);
    return () => {
      sessionPlayer.off('adBreakStart', onAdBreakStartHandler);
      sessionPlayer.off('adBreakEnd', onAdBreakEndHandler);
      sessionPlayer.off('adTime', onAdTime);
    };
  }, [sessionPlayer, onAdBreakStart, onAdBreakEnd]);

  useEffect(() => {
    if (!sessionPlayer || !analyticsToken) return undefined;
    const onAdImpression = () => {
      const jwpltx = 'jwpltx' in globalThis ? (globalThis as typeof globalThis & { jwpltx?: Jwpltx }).jwpltx : undefined;
      jwpltx?.adImpression();
    };
    sessionPlayer.on('adImpression', onAdImpression);
    return () => sessionPlayer.off('adImpression', onAdImpression);
  }, [sessionPlayer, analyticsToken]);

  const handleSkipAd = useEventCallback(() => {
    sessionPlayer?.skipAd?.();
  });

  const sidecarCaptionTracksRaw = item.tracks?.filter((t) => t.kind === 'captions' || t.kind === 'subtitles') ?? [];
  const sidecarCaptionTracks = useMemo(
    () =>
      sidecarCaptionTracksRaw.map((track) => ({
        uri: track.file,
        language: 'en',
        kind: (track.kind === 'captions' ? 'captions' : 'subtitles') as 'captions' | 'subtitles',
        label: track.label || (track.kind === 'captions' ? 'Captions' : 'Subtitles'),
      })),
    [sidecarCaptionTracksRaw],
  );
  const primaryCaption = sidecarCaptionTracksRaw[0];
  const extraCaptionTracks = sidecarCaptionTracksRaw.slice(1);
  /** JWP sidecar VTT URL is forwarded to the SDK, which side-loads it into Shaka after `load()`. */
  const trickplayThumbnailsUrl = item.tracks?.find((t) => t.kind === 'thumbnails')?.file ?? null;
  /** JWP-style WebVTT chapters (`tracks[].kind === 'chapters'`) — fetched and parsed in `CustomPlayerChapters`. */
  const chapterTrack = item.tracks?.find((t) => t.kind === 'chapters');
  const chaptersUrl = !isLiveStream && chapterTrack?.file ? chapterTrack.file : null;

  const showLoader = (s: { loadError: string | null; isBuffering: boolean; isReconnecting?: boolean }) =>
    !isAdPlaying &&
    !s.loadError &&
    !s.isReconnecting &&
    (!manifestUrl || (!initialLoadComplete && s.isBuffering));

  const handleOpenMoreLikeThis = useEventCallback((video?: HTMLVideoElement | null) => {
    sessionPlayer?.pause();
    video?.pause();
    setShowMoreLikeThisOverlay(true);
  });

  const updateMoreLikeThisScrollState = useEventCallback(() => {
    const row = moreLikeThisRowRef.current;
    if (!row) {
      setCanScrollMoreLikeThisLeft(false);
      setCanScrollMoreLikeThisRight(false);
      return;
    }

    const maxScrollLeft = row.scrollWidth - row.clientWidth;
    setCanScrollMoreLikeThisLeft(row.scrollLeft > 1);
    setCanScrollMoreLikeThisRight(maxScrollLeft - row.scrollLeft > 1);
  });

  const handleMoreLikeThisScroll = useEventCallback((direction: 'left' | 'right') => {
    const row = moreLikeThisRowRef.current;
    if (!row) return;

    const delta = Math.max(row.clientWidth * 0.8, 240);
    row.scrollBy({ left: direction === 'right' ? delta : -delta, behavior: 'smooth' });
  });

  useEffect(() => {
    if (!showMoreLikeThisOverlay) return undefined;

    const row = moreLikeThisRowRef.current;
    if (!row) return undefined;

    updateMoreLikeThisScrollState();

    const onScroll = () => updateMoreLikeThisScrollState();
    const onResize = () => updateMoreLikeThisScrollState();

    row.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    return () => {
      row.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
    };
  }, [showMoreLikeThisOverlay, relatedVideos.length, updateMoreLikeThisScrollState]);

  return (
    <Player
      manifestUrl={manifestUrl}
      drmConfig={drmConfig}
      isLiveStream={isLiveStream}
      playlistItem={item}
      startTime={startTime}
      autostart={autostart}
      adType={isCsai ? 'csai' : 'none'}
      adsConfig={adsConfig}
      mediaKey={playbackMediaKey}
      onReady={handleReady}
      onBeforePlay={onBeforePlay}
      onTeardown={onRemove}
      containerClassName={[
        styles.container,
        isAdPlaying ? styles.adPlaying : '',
        showMoreLikeThisOverlay ? styles.moreLikeThisOpen : '',
      ].filter(Boolean).join(' ')}
      shellTestId={testId('custom-player')}
      videoClassName={styles.video}
      videoProps={{
        playsInline: true,
        poster: videoThumnail,
        tabIndex: 0,
      }}
      trickplayThumbnailsUrl={isLiveStream ? null : trickplayThumbnailsUrl}
      sidecarCaptionTracks={sidecarCaptionTracks}
      videoChildren={
        <>
          <track
            kind="captions"
            src={primaryCaption?.file ?? PLACEHOLDER_CAPTIONS_TRACK}
            srcLang="en"
            label={primaryCaption?.label || 'Captions'}
          />
          {extraCaptionTracks.map((t) => (
            <track key={t.file} kind="subtitles" src={t.file} srcLang="en" label={t.label || 'Subtitles'} />
          ))}
        </>
      }
    >
      {({ state, videoRef, containerRef, shakaPlayerRef }) => (
        <>
          <CustomPlayerOverlays
            state={state}
            videoRef={videoRef}
            containerRef={containerRef}
            shakaPlayerRef={shakaPlayerRef}
            chaptersUrl={chaptersUrl}
            isLiveStream={isLiveStream}
            hidden={isAdPlaying}
          />
          {showLoader(state) ? (
            <div className={styles.loader}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" focusable="false">
                <path d="M120,186.667a66.667,66.667,0,0,1,0-133.333V40a80,80,0,1,0,80,80H186.667A66.846,66.846,0,0,1,120,186.667Z" />
              </svg>
            </div>
          ) : null}
          {/* Single reconnecting label during live error retries — never alongside error/loader. */}
          {state.isReconnecting && !state.loadError ? (
            <div className={styles.reconnecting} aria-live="polite" data-testid={testId('custom-player-reconnecting')}>
              {ReConnecting}
            </div>
          ) : null}
          {/* {isLiveStream && !state.loadError && <LiveBadge />} */}
          {state.loadError && !state.isReconnecting ? <div className={styles.error}>{state.loadError}</div> : null}
          {adPlayback && adPlayback.duration > 0 ? (
            <CustomPlayerAdChrome ad={adPlayback} onSkip={handleSkipAd} />
          ) : null}
          {showMoreLikeThisButton && relatedVideos.length > 0 && !showMoreLikeThisOverlay && !isAdPlaying && !state.loadError ? (
            <MoreLikeThisButton
              containerRef={containerRef}
              label={t('more_like_this', { defaultValue: 'More Like This' })}
              onClick={handleOpenMoreLikeThis}
              videoRef={videoRef}
            />
          ) : null}
          {showMoreLikeThisOverlay && !isAdPlaying && !state.loadError ? (
            <div className={styles.moreLikeThisOverlay}>
              <div className={styles.moreLikeThisHeader}>
                <h2 className={styles.moreLikeThisTitle}>{t('more_like_this', { defaultValue: 'More Like This' })}</h2>
                <button
                  type="button"
                  className={styles.moreLikeThisClose}
                  aria-label={t('common:close', { defaultValue: 'Close' })}
                  onClick={() => setShowMoreLikeThisOverlay(false)}
                >
                  <Icon icon={Close} />
                </button>
              </div>
              <div className={styles.moreLikeThisCarousel}>
                <button
                  type="button"
                  className={styles.moreLikeThisNavButton}
                  aria-label={t('common:slide_previous', { defaultValue: 'Previous' })}
                  onClick={() => handleMoreLikeThisScroll('left')}
                  disabled={!canScrollMoreLikeThisLeft}
                >
                  <Icon icon={ChevronLeft} />
                </button>
                <div ref={moreLikeThisRowRef} className={styles.moreLikeThisRow}>
                  {relatedVideos.map((relatedItem) => (
                    <button
                      key={relatedItem.mediaid}
                      type="button"
                      className={styles.moreLikeThisCard}
                      onClick={() => onRelatedVideoClick?.(relatedItem)}
                    >
                      <span className={styles.moreLikeThisImageWrap}>
                        <img
                          className={styles.moreLikeThisImage}
                          src={relatedItem.cardImage || relatedItem.image}
                          alt=""
                        />
                        {relatedItem.duration ? <span className={styles.moreLikeThisDuration}>{formatDurationTag(relatedItem.duration)}</span> : null}
                      </span>
                      <span className={styles.moreLikeThisCardTitle}>{relatedItem.title}</span>
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.moreLikeThisNavButton}
                  aria-label={t('common:slide_next', { defaultValue: 'Next' })}
                  onClick={() => handleMoreLikeThisScroll('right')}
                  disabled={!canScrollMoreLikeThisRight}
                >
                  <Icon icon={ChevronRight} />
                </button>
              </div>
            </div>
          ) : null}
          {isAutoPlayNextVideo && nextItem && nextCountdownTime !== null && showNextOverlay && !isAdPlaying && (
            <NextCountdownOverlay
              nextCountdownTime={nextCountdownTime}
              handleCloseOverlay={handleCloseOverlay}
              handleSkipToEnd={handleSkipToEnd}
              nextVideoInTime={nextVideoInTime}
              nextItem={nextItem}
            />
          )}
        </>
      )}
    </Player>
  );
};

export default CustomPlayer;
